import { Button } from '@lmig/lmds-react-button';
import './QuoteModal.scss';
import { useContext, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lmig/lmds-react-modal';
import { getBLQuoteLink, getClientList } from 'Foundation/Configurations/GlobalConfiguration';
import { useUserData } from 'Foundation/User/UserContext';
import { Heading } from '@lmig/lmds-react-typography';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import { GetSiteStates, IStateObject } from 'Foundation/Common/StateHelper';
import { FieldGroup } from '@lmig/lmds-react-field-group';
import { Link } from '@lmig/lmds-react-link';
import { IQuoteContext, QuoteContext } from './QuoteContext';

interface QuoteModalProps {
  open?: boolean;
  state?: string;
  onClose?: () => void;
}

export default function QuoteModal({ state, open, onClose }: QuoteModalProps) {
  const [isOpen, setIsOpen] = useState<boolean>();
  const { userData } = useUserData();
  const states: IStateObject[] = GetSiteStates('PL');
  const quoteContext = useContext<IQuoteContext>(QuoteContext);
  const [products, setProducts] = useState<IProduct[] | null>();
  const [selectedState, setSelectedState] = useState<string | undefined>();
  const [sections, setSections] = useState<string[]>();

  useEffect(() => {
    if (!products) quoteContext.setSending();
    if (userData?.state) setSelectedState(userData.state);
    if (state) setSelectedState(state);
    if (open) setIsOpen(open);
  }, []);

  useEffect(() => {
    if (quoteContext.products && selectedState)
      setProducts(quoteContext.products.filter((x) => x.States?.includes(selectedState)));
  }, [quoteContext.products, selectedState]);

  useEffect(() => {
    if (!sections && products)
      setSections(products.map((x) => x.Category).filter((value, index, array) => array.indexOf(value) === index));
  }, [products]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          if (onClose) onClose();
        }}
        size="large"
        id="pl-quote-modal"
      >
        <ModalHeader>
          <Heading type="h3-light">Get a quote</Heading>
        </ModalHeader>
        <ModalBody>
          <div id="qm-select-a-state">
            <FieldGroup labelVisual="Selected state (if necessary)" id="qm-state-select">
              <div data-theme="evolved">
                <Select
                  defaultValue={selectedState}
                  labelVisual="Selected state"
                  onChange={(event) => setSelectedState(event.currentTarget.value)}
                >
                  {states.map((x) => (
                    <SelectOption value={x.Abbreviation}>{x.Name}</SelectOption>
                  ))}
                </Select>
              </div>
            </FieldGroup>
          </div>
          {products && products.length ? (
            <div id="qm-lower-section">
              <Heading type="h4-light">Choose an insurance product</Heading>
              <div className="qm-product-sections">
                {sections &&
                  sections.map((x) =>
                    products.length ? (
                      <div className="qm-product-section">
                        <div className="qm-section-title">
                          <Heading type="h5">{x}</Heading>
                        </div>
                        <div className="qm-product-list">
                          {products &&
                            products
                              .filter((y) => y.Category == x)
                              .map((z) =>
                                z.Name == 'Earthquake' ? (
                                  <div>
                                    <div>Earthquake</div>
                                    <p className="earthquake-notice">
                                      For CA, OR, and WA earthquake quotes, contact the Licensed Sales Center at{' '}
                                      <Link href="tel:888-451-8107">888-451-8107</Link>.
                                    </p>
                                  </div>
                                ) : (
                                  <Link
                                    href={z.Link.replace('$STATE', selectedState ?? '')}
                                    variant="standalone"
                                    caretPosition="none"
                                    target="_blank"
                                  >
                                    {z.Name}
                                  </Link>
                                ),
                              )}
                        </div>
                      </div>
                    ) : (
                      <></>
                    ),
                  )}
              </div>
            </div>
          ) : (
            <Heading type="h4-light" id="qm-no-products-message">
              Sorry, we do not currently quote products in this state. Please select another state.
            </Heading>
          )}
        </ModalBody>
        <ModalFooter>
          <p>
            To find an existing quote or customer, use{' '}
            <Link href={getClientList()} target="_blank">
              client list
            </Link>
          </p>
        </ModalFooter>
      </Modal>
      {!userData?.isPL() || (!userData && window.location.pathname.includes('/BL/')) ? (
        <div className="bl-tabMobile" data-theme="evolved">
          <Button
            id="bl-quote-button"
            href={getBLQuoteLink(userData?.isAgent() ?? true)}
            variant="primary"
            size="large"
            target="_blank"
            className="desktop-header"
          >
            Quote or endorse
          </Button>
          <Button
            id="bl-quote-button-tabMobile"
            href={getBLQuoteLink(userData?.isAgent() ?? true)}
            variant="primary"
            size="large"
            target="_blank"
            className="mobile-header"
          >
            Quote or endorse
          </Button>
        </div>
      ) : (
        <div data-theme="evolved">
          <Button
            id="pl-quote-button"
            variant="primary"
            size="large"
            className="desktop-header"
            onClick={() => setIsOpen(true)}
          >
            Get a quote
          </Button>
          <Button
            id="pl-quote-button-tabMobile"
            variant="primary"
            size="large"
            className="mobile-header"
            onClick={() => setIsOpen(true)}
          >
            Get a quote
          </Button>
        </div>
      )}
    </>
  );
}
